import React, { Suspense, useEffect, useState } from "react";
import "@react-pdf-viewer/core/lib/styles/index.css"; // Core styles
import { Worker, Viewer } from "@react-pdf-viewer/core";
import { useNavigate, useSearchParams } from "react-router-dom"; // For navigating to Fee Summary
import { PDFDocument } from "pdf-lib"; // Import PDFDocument from pdf-lib
import editPdf from "../../lib/editPdf";
// import { services } from "./data";
import { Button } from "@mui/material";
import ServiceTablePDF from "./projectTable";
import { pdf } from "@react-pdf/renderer";
import ProposalNextSteps from "./LastPage";
import { useApp } from "../../Context/AppContext";
import { viewProject } from "../../apis/apisList/userApi";
import CustomLoader from "../../components/CustomLoader";
import { toast } from "react-toastify";
const PDFViewer = () => {
  const navigate = useNavigate();
  const { pdfData, setPdfData, acceptedDate, setAcceptedDate } = useApp();
  const [isLastPage, setIsLastPage] = useState(false);
  const [scale, setScale] = useState(1.5);
  const [searchParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [services, setServices] = useState([]);
  const projectId = searchParams.get("id") || null;
  const token = searchParams.get("token") || null;

  useEffect(() => {
    const getProject = async () => {
      if (!projectId) return;
      setIsLoading(true);
      try {
        let res = await viewProject(projectId, token);
        setData(res.data);
        if (res.data.services) {
          setServices(res.data.services);
        }
        const { user_accepted_date } = res.data.proposalInfo;
        if (user_accepted_date) {
          setAcceptedDate(user_accepted_date);
        }
      } catch (error) {
        toast.error(
          error?.response?.data?.message || "Error fetching project",
          {
            toastId: "fetch-project-error", // unique ID for this type of error
            autoClose: 5000,
            position: "top-center",
          }
        );
        console.error("Error fetching project:", error);
      } finally {
        setIsLoading(false);
      }
    };

    getProject();
  }, []);

  useEffect(() => {
    // Adjust scale based on screen size
    const handleResize = () => {
      const width = window.innerWidth;
      if (width <= 600) {
        setScale(0.7); // Mobile
      } else if (width <= 1024) {
        setScale(1.2); // Tablet
      } else {
        setScale(1.5); // Desktop
      }
    };

    handleResize(); // Initial scale
    window.addEventListener("resize", handleResize);

    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const generateServiceTablePdf = async (serviceData, index, count) => {
    const pdfDoc = await pdf(
      <ServiceTablePDF service={serviceData} index={index} count={count} />
    ).toBlob();
    return new Uint8Array(await pdfDoc.arrayBuffer());
  };
  const generateLastPage = async () => {
    const pdfDoc = await pdf(<ProposalNextSteps />).toBlob();
    return new Uint8Array(await pdfDoc.arrayBuffer());
  };

  useEffect(() => {
    if (!data) return;
    const mergePdfs = async () => {
      try {
        setIsLoading(true);
        // Generate initial PDF components
        const editedPdfBytes = await editPdf(data, acceptedDate);
        const response = await fetch("/proposal_data.pdf");
        const originalPdfBytes = await response.arrayBuffer();

        // Validate initial PDF inputs
        if (!editedPdfBytes) throw new Error("Failed to edit the PDF!");
        if (
          !(editedPdfBytes instanceof Uint8Array) &&
          !(editedPdfBytes instanceof ArrayBuffer)
        ) {
          throw new TypeError(
            "Edited PDF must be of type Uint8Array or ArrayBuffer"
          );
        }
        if (!(originalPdfBytes instanceof ArrayBuffer)) {
          throw new TypeError("Original PDF must be an ArrayBuffer");
        }

        // Create a new PDF document for merging
        const pdfDoc = await PDFDocument.create();

        // 1. Add the edited PDF (first page only)
        const editedPdf = await PDFDocument.load(editedPdfBytes);
        const [editedPage] = await pdfDoc.copyPages(editedPdf, [0]);
        pdfDoc.addPage(editedPage);

        // 2. Add all pages from the original proposal
        const originalPdf = await PDFDocument.load(originalPdfBytes);
        const originalPages = await pdfDoc.copyPages(
          originalPdf,
          originalPdf.getPageIndices()
        );
        originalPages.forEach((page) => pdfDoc.addPage(page));

        // 3. Add ServiceTable PDFs for each service
        let index = 0;
        for (const service of services) {
          try {
            const serviceTablePdfBytes = await generateServiceTablePdf(
              service,
              index,
              services.length
            );
            if (!serviceTablePdfBytes) {
              console.warn("Skipping service - failed to generate PDF");
              continue;
            }

            const serviceTablePdf = await PDFDocument.load(
              serviceTablePdfBytes
            );
            const [serviceTablePage] = await pdfDoc.copyPages(serviceTablePdf, [
              0,
            ]);
            pdfDoc.addPage(serviceTablePage);
          } catch (error) {
            console.error(
              `Failed to add service ${service.id || service.name}:`,
              error
            );
          }
          index++;
        }

        const lastPageByte = await generateLastPage();
        const lastPdf = await PDFDocument.load(lastPageByte);
        const [lastPage] = await pdfDoc.copyPages(lastPdf, [0]);
        pdfDoc.addPage(lastPage);

        // Save and create download URL
        const mergedPdfBytes = await pdfDoc.save();
        const mergedPdfBlob = new Blob([mergedPdfBytes], {
          type: "application/pdf",
        });
        setPdfData(URL.createObjectURL(mergedPdfBlob));
        setIsLoading(false);
      } catch (error) {
        setIsLoading(false);
        console.error("Failed to merge PDFs:", error);
      }
    };

    mergePdfs();
  }, [acceptedDate, scale, data]); // Add services to dependency array

  if (isLoading) {
    return <CustomLoader loading={isLoading} />;
  }

  if (!data || !pdfData) {
    return <p>Project data not found.</p>;
  }

  return (
    <>
      <Button
        variant="contained"
        onClick={() => {
          const link = document.createElement("a");
          link.href = pdfData;
          link.download = "Proposal.pdf";
          link.click();
        }}
        sx={{
          margin: "10px",
          backgroundColor: "#1976d2",
          color: "#fff",
          border: "none",
          borderRadius: "4px",
          cursor: "pointer",
          fontSize: "16px",
          fontWeight: "bold",
          textTransform: "uppercase",
          "&:hover": {
            backgroundColor: "#1565c0",
          },
        }}
      >
        Download PDF
      </Button>

      <div>
        <Worker workerUrl="https://unpkg.com/pdfjs-dist@3.11.174/build/pdf.worker.min.js">
          <Viewer fileUrl={pdfData} defaultScale={scale} enableSmoothScroll />
        </Worker>
        {/* {!isLoading && (
          <div style={{ textAlign: "center", padding: "20px" }}>
            <button
              onClick={() => {
                if (acceptedDate) return;
                window.scrollTo({
                  top: 0,
                });
                navigate(`/proposal/summary?id=${projectId}`);
              }}
              style={{
                padding: "10px 20px",
                backgroundColor: acceptedDate ? "#50C878" : "#1976d2",
                color: "#fff",
                border: "none",
                borderRadius: "4px",
                cursor: "pointer",
                fontSize: "16px",
              }}
              disabled={!!acceptedDate}
            >
              {acceptedDate ? "Accepted" : "Approve"}
            </button>
          </div>
        )} */}
      </div>
    </>
  );
};

export default PDFViewer;
